import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertOctagon, AlertTriangle, Bell, BellOff, CheckCheck, Info, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/widgets/Pill';
import { useAlertsStore, formatRiskDetail, type Alert, type AlertSeverity } from '@/features/alerts/store';
import { adminRiskEvents } from '@/api/admin';
import type { RiskEvent, RiskEventSeverity } from '@/api/types';
import { fmtDateTime } from '@/lib/format';
import { ApiError } from '@/lib/http';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';

const SEV_ICON: Record<AlertSeverity, React.ReactNode> = {
  INFO: <Info className="h-4 w-4 text-brand" />,
  WARN: <AlertTriangle className="h-4 w-4 text-warn" />,
  CRIT: <AlertOctagon className="h-4 w-4 text-down" />,
};

const SEV_TONE: Record<AlertSeverity, 'brand' | 'warn' | 'down'> = {
  INFO: 'brand',
  WARN: 'warn',
  CRIT: 'down',
};

const SEV_BORDER: Record<AlertSeverity, string> = {
  INFO: 'border-l-brand',
  WARN: 'border-l-warn',
  CRIT: 'border-l-down',
};

const TITLE_BY_TYPE: Record<string, string> = {
  ORDER_BLOCKED: '跟单引擎拦截下单',
  EMERGENCY_STOP: '风控自动触发紧急停机',
  FORCE_LIQUIDATION: '一键强制平仓已触发',
  ABNORMAL_VOLUME: '异常单量',
  MARGIN_HIGH: '保证金占用接近上限',
  EQUITY_LOW: '净值跌破水位线',
  API_ERROR_SPIKE: 'cTrader API 错误率异常',
};

/** 后端 severity('CRITICAL')→ UI 内部('CRIT')。 */
function toUiSeverity(s: RiskEventSeverity): AlertSeverity {
  return s === 'CRITICAL' ? 'CRIT' : s;
}

/** UI 内部 severity('CRIT')→ 后端查询参数('CRITICAL')。 */
function toApiSeverity(s: AlertSeverity): RiskEventSeverity {
  return s === 'CRIT' ? 'CRITICAL' : s;
}

/** RiskEvent → 统一 Alert 视图模型(createdAt → occurredAt,CRITICAL → CRIT)。 */
function riskEventToAlert(e: RiskEvent): Alert {
  return {
    id: e.id,
    type: e.type as Alert['type'],
    severity: toUiSeverity(e.severity),
    title: TITLE_BY_TYPE[e.type] ?? e.type,
    detail: formatRiskDetail(e.type, e.payload),
    occurredAt: e.createdAt,
    ack: !!e.acknowledgedAt,
    ackAt: e.acknowledgedAt,
    payload: e.payload,
  };
}

type Tab = 'active' | 'history';
const PAGE_SIZE = 50;

export function AlertsCenter() {
  const alerts = useAlertsStore((s) => s.alerts);
  const ackLocal = useAlertsStore((s) => s.ack);
  const ackAll = useAlertsStore((s) => s.ackAll);
  const clear = useAlertsStore((s) => s.clear);
  const qc = useQueryClient();

  const [tab, setTab] = useState<Tab>('active');
  const [sevFilter, setSevFilter] = useState<AlertSeverity | 'ALL'>('ALL');
  const [historyAckFilter, setHistoryAckFilter] = useState<'ALL' | 'ACK' | 'UNACK'>('ALL');
  const [page, setPage] = useState(1);

  // 活跃告警:WS 实时推送累积(zustand 内存),未确认的为活跃。
  const active = useMemo(() => alerts.filter((a) => !a.ack), [alerts]);

  // 历史告警:从 risk_events 表持久化读取(分页 + severity/已确认筛选)。
  const historyQuery = useQuery({
    queryKey: [
      'admin',
      'risk-events',
      { page, severity: sevFilter, ack: historyAckFilter },
    ],
    queryFn: () =>
      adminRiskEvents.list({
        page,
        pageSize: PAGE_SIZE,
        ...(sevFilter !== 'ALL' ? { severity: toApiSeverity(sevFilter) } : {}),
        ...(historyAckFilter === 'ACK'
          ? { acknowledged: true }
          : historyAckFilter === 'UNACK'
            ? { acknowledged: false }
            : {}),
      }),
    enabled: tab === 'history',
    placeholderData: (prev) => prev,
  });

  const ackMut = useMutation({
    mutationFn: (id: string) => adminRiskEvents.ack(id),
    onSuccess: () => {
      toast.success('已确认并归档');
      qc.invalidateQueries({ queryKey: ['admin', 'risk-events'] });
    },
    onError: (err) => {
      toast.error('确认失败', { description: err instanceof ApiError ? err.message : '请稍后重试' });
    },
  });

  const historyItems = useMemo(
    () => (historyQuery.data?.items ?? []).map(riskEventToAlert),
    [historyQuery.data],
  );
  const historyTotal = historyQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(historyTotal / PAGE_SIZE));

  // 活跃 tab 的 severity 筛选仍走内存。
  const visibleActive = useMemo(() => {
    if (sevFilter === 'ALL') return active;
    return active.filter((a) => a.severity === sevFilter);
  }, [sevFilter, active]);

  const sevCount = (sev: AlertSeverity) => active.filter((a) => a.severity === sev).length;

  const switchTab = (t: Tab) => {
    setTab(t);
    setPage(1);
  };
  const switchSev = (s: AlertSeverity | 'ALL') => {
    setSevFilter(s);
    setPage(1);
  };

  const visible = tab === 'active' ? visibleActive : historyItems;

  return (
    <div className="p-6 space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
            <Bell className="h-5 w-5 text-brand" />
            告警中心
          </h1>
          <p className="text-sm text-ink-2 mt-1">
            活跃告警(实时订阅 risk_events 频道) / 历史告警(持久化查询) · 严重等级配色(§9.14)
          </p>
        </div>
        <div className="flex items-center gap-2">
          {active.length > 0 && (
            <Button variant="outline" size="sm" onClick={ackAll}>
              <CheckCheck className="h-4 w-4" />
              全部已确认(本地)
            </Button>
          )}
          {alerts.some((a) => a.ack) && (
            <Button variant="ghost" size="sm" onClick={clear} className="text-ink-3">
              <Trash2 className="h-4 w-4" />
              清空本地缓存
            </Button>
          )}
        </div>
      </header>

      {/* 严重等级 KPI(基于实时活跃告警) */}
      <section className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <SevKpi label="活跃告警" value={active.length} tone={active.length > 0 ? 'down' : 'brand'} icon={<Bell className="h-4 w-4" />} />
        <SevKpi label="CRIT 严重" value={sevCount('CRIT')} tone="down" icon={<AlertOctagon className="h-4 w-4" />} />
        <SevKpi label="WARN 警告" value={sevCount('WARN')} tone="warn" icon={<AlertTriangle className="h-4 w-4" />} />
        <SevKpi label="INFO 信息" value={sevCount('INFO')} tone="brand" icon={<Info className="h-4 w-4" />} />
      </section>

      {/* Tabs + Sev filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-md border border-line bg-bg-1 p-0.5">
          <button
            onClick={() => switchTab('active')}
            className={cn(
              'px-4 py-1.5 text-sm rounded-sm transition-colors',
              tab === 'active' ? 'bg-brand text-white' : 'text-ink-2 hover:text-ink-1',
            )}
          >
            活跃 · {active.length}
          </button>
          <button
            onClick={() => switchTab('history')}
            className={cn(
              'px-4 py-1.5 text-sm rounded-sm transition-colors',
              tab === 'history' ? 'bg-brand text-white' : 'text-ink-2 hover:text-ink-1',
            )}
          >
            历史{tab === 'history' && historyQuery.data ? ` · ${historyTotal}` : ''}
          </button>
        </div>

        {tab === 'history' && (
          <div className="inline-flex rounded-md border border-line bg-bg-1 p-0.5">
            {([
              ['ALL', '全部'],
              ['UNACK', '未确认'],
              ['ACK', '已确认'],
            ] as const).map(([v, label]) => (
              <button
                key={v}
                onClick={() => {
                  setHistoryAckFilter(v);
                  setPage(1);
                }}
                className={cn(
                  'px-3 py-1.5 text-xs rounded-sm transition-colors',
                  historyAckFilter === v ? 'bg-bg-3 text-ink-0' : 'text-ink-3 hover:text-ink-1',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-1.5 ml-auto">
          {(['ALL', 'CRIT', 'WARN', 'INFO'] as const).map((s) => (
            <Button
              key={s}
              variant={sevFilter === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => switchSev(s)}
            >
              {s === 'ALL' ? '全部' : s}
            </Button>
          ))}
        </div>
      </div>

      {/* List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">
            {tab === 'active' ? '当前活跃告警' : '历史风控事件(持久化)'} · {visible.length} 条
            {tab === 'history' && historyTotal > 0 && (
              <span className="ml-2 text-ink-3 font-normal">第 {page}/{totalPages} 页</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {tab === 'history' && historyQuery.isError && (
            <div className="px-1 py-3 text-sm text-down">
              无法加载历史告警:{historyQuery.error instanceof ApiError ? historyQuery.error.message : '未知错误'}
            </div>
          )}
          {tab === 'history' && historyQuery.isLoading && (
            <div className="flex items-center justify-center py-12 text-ink-3">加载中...</div>
          )}
          {visible.length === 0 && !(tab === 'history' && historyQuery.isLoading) && (
            <div className="flex flex-col items-center justify-center py-12 text-center text-ink-3 gap-2">
              <BellOff className="h-8 w-8" />
              {tab === 'active' ? '当前没有活跃告警 — 系统运行正常' : '没有符合条件的历史告警'}
            </div>
          )}
          {groupByTime(visible).map((group) => (
            <div key={group.label} className="space-y-2">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-ink-3 px-1">
                <span>{group.label}</span>
                <span className="num">{group.items.length}</span>
                <div className="flex-1 h-px bg-line" />
              </div>
              {group.items.map((a) => (
                <div
                  key={a.id}
                  className={cn(
                    'rounded-md border border-line bg-bg-2 p-3 flex items-start gap-3 border-l-4',
                    SEV_BORDER[a.severity],
                    !a.ack && a.severity === 'CRIT' && 'animate-pulse-glow',
                  )}
                >
                  <div className="mt-0.5 shrink-0">{SEV_ICON[a.severity]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Pill tone={SEV_TONE[a.severity]} className="text-[10px]">
                        {a.severity}
                      </Pill>
                      <span className="text-sm font-semibold text-ink-0 truncate">{a.title}</span>
                      <span className="text-xs text-ink-3 num">{a.type}</span>
                    </div>
                    {a.detail && <div className="mt-1 text-xs text-ink-2 num break-words">{a.detail}</div>}
                    <div className="mt-1.5 text-[11px] text-ink-3 num flex items-center gap-2 flex-wrap">
                      <span>发生:{fmtDateTime(a.occurredAt)}</span>
                      {a.ack && a.ackAt && <span>· 已确认 {fmtDateTime(a.ackAt)}</span>}
                    </div>
                  </div>
                  {!a.ack &&
                    (tab === 'history' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={ackMut.isPending}
                        onClick={() => ackMut.mutate(a.id)}
                      >
                        已确认
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => ackLocal(a.id)}>
                        已确认
                      </Button>
                    ))}
                </div>
              ))}
            </div>
          ))}

          {tab === 'history' && historyTotal > PAGE_SIZE && (
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || historyQuery.isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                上一页
              </Button>
              <span className="text-xs text-ink-3 num">{page} / {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || historyQuery.isFetching}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                下一页
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function groupByTime(items: Alert[]): Array<{ label: string; items: Alert[] }> {
  if (items.length === 0) return [];
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 86400_000;
  const weekAgo = today - 6 * 86400_000;
  const groups: Record<string, Alert[]> = {};
  const order: string[] = [];
  const push = (label: string, a: Alert) => {
    if (!groups[label]) {
      groups[label] = [];
      order.push(label);
    }
    groups[label].push(a);
  };
  for (const a of items) {
    const t = new Date(a.occurredAt).getTime();
    if (t >= today) push('今天', a);
    else if (t >= yesterday) push('昨天', a);
    else if (t >= weekAgo) push('本周', a);
    else push('更早', a);
  }
  return order.map((label) => ({ label, items: groups[label]! }));
}

function SevKpi({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: number;
  tone: 'brand' | 'warn' | 'down';
  icon: React.ReactNode;
}) {
  const cls = { brand: 'text-brand', warn: 'text-warn', down: 'text-down' }[tone];
  return (
    <div className="rounded-lg border border-line bg-bg-2 p-4 elev-card">
      <div className="flex items-start justify-between">
        <div className="text-xs uppercase tracking-wide text-ink-2">{label}</div>
        <div className={cls}>{icon}</div>
      </div>
      <div className={cn('mt-2 text-2xl font-bold num', cls)}>{value}</div>
    </div>
  );
}
